"use client";

import { useRef } from "react";
import {
  Button,
  Eyebrow,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { useMaskedReveal } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import IsoSceneStage from "@/components/iso/IsoSceneStage";
import { SECURITY_SCENE } from "./security-iso";

/**
 * /security hero — light on-grid, left-aligned (handoff design). One claim
 * block: eyebrow → h1 → lead → CTAs → the mono scroll invite that hands the
 * reader straight to the signature section. At lg+ the sealed-boundary iso
 * scene sits beside the copy as the second column. Page-only and always
 * inside `RefSections onGrid`, so the row-layout hooks run unconditionally.
 */
export default function SecurityHero() {
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h1", sub: "[data-hero-sub]" });

  return (
    <SectionShell id="sec-hero" decoAvoid className="md:pt-px md:pb-px">
      <div ref={rootRef} className="pt-24 pb-4 md:pb-0 md:pt-[calc(2*var(--grid-cell,72px))]">
        <div className="flex flex-col gap-12 lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center lg:gap-6">
          <div data-grid-block>
            <div ref={copyRef}>
              <div data-hero-sub className="flex">
                <Eyebrow>Security</Eyebrow>
              </div>
              <h1 className="mt-[22px] max-w-[760px] text-[clamp(34px,4.5vw,48px)] font-extrabold leading-[1.06] tracking-[-0.03em]">
                Safe, private, and verifiable.
              </h1>
              <p
                data-hero-sub
                className="mt-[18px] max-w-[580px] text-[17px] leading-[1.6] text-muted [text-wrap:pretty]"
              >
                Every claim on this page is something we can show you — not just tell
                you. Facts, dates and named things, for the person filling in the
                questionnaire.
              </p>
              <div data-hero-sub className="mt-7 flex flex-wrap gap-3">
                <Button href={`${routePath("contact")}?intent=security`}>Talk to security</Button>
                <Button href={routePath("legalPrivacy")} variant="ghost">
                  Privacy policy
                </Button>
              </div>
              <div data-hero-sub className="mt-11">
                <a
                  href="#request-path"
                  className="inline-flex items-center gap-2 font-jetbrains text-[11.5px] text-muted transition-colors hover:text-brand-600"
                >
                  ↓ follow one request through the boundary, hop by hop
                </a>
              </div>
            </div>
          </div>

          {/* The sealed boundary — the whole fortress at a glance; the spine
              below walks into it hop by hop. Floats on the grid (no clearing). */}
          <div className="relative lg:-mr-6 xl:-mr-14">
            <p className="sr-only">
              Illustration: the rii.link routing core sealed behind its security
              boundary — TLS in front, abuse checks, the tenant-partitioned
              database, EU-first handling with visitor IPs anonymised before storage,
              and the request log.
            </p>
            <IsoSceneStage
              {...SECURITY_SCENE}
              surfaceRef={rootRef}
              connectorStroke="rgba(240,96,144,0.55)"
              nodeFill="var(--color-brand-400)"
              nodeStroke="rgba(240,96,144,0.35)"
              className="pointer-events-none mx-auto w-full max-w-[560px] lg:max-w-none"
            />
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
